/**
 * Utility functions for image optimization and processing
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { resolveColivingImageUrl } from './colivingImageUrl';

/**
 * Calculate aspect ratio dimensions while maintaining proportions
 */
function calculateAspectRatio(srcWidth, srcHeight, maxWidth, maxHeight) {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
  return {
    width: Math.round(srcWidth * ratio),
    height: Math.round(srcHeight * ratio)
  };
}

/**
 * Optimize image by creating multiple sizes with different quality settings
 */
export async function optimizeImage(file) {
  if (!file.type.startsWith('image/')) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Create different sizes with better quality
        const sizes = [
          { width: 1920, height: 1080, suffix: 'large', quality: 0.95 },  // Higher quality for main image
          { width: 800, height: 600, suffix: 'medium', quality: 0.9 },
          { width: 400, height: 300, suffix: 'small', quality: 0.85 }
        ];

        const optimizedImages = sizes.map(size => {
          const canvas = document.createElement('canvas');
          let { width, height } = calculateAspectRatio(img.width, img.height, size.width, size.height);
          
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          // Use better image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          return new Promise((resolve) => {
            canvas.toBlob(
              (blob) => {
                resolve({
                  file: new File([blob], `${file.name.replace(/\.[^/.]+$/, '')}_${size.suffix}.jpg`, {
                    type: 'image/jpeg'  // Use JPEG instead of WebP for better browser support
                  }),
                  size: size.suffix
                });
              },
              'image/jpeg',  // Use JPEG format
              size.quality   // Use size-specific quality
            );
          });
        });

        Promise.all(optimizedImages).then(resolve);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// Función para hacer públicas todas las imágenes de contratos
export async function makeAllContractImagesPublic() {
  try {
    const functions = getFunctions();
    const makePublic = httpsCallable(functions, 'makeContractImagesPublic');
    
    const result = await makePublic();
    return result.data;
  } catch (error) {
    console.error('Error al hacer públicas las imágenes:', error);
    throw error;
  }
}

// Función para actualizar las URLs en los contratos de Firestore
export async function updateContractImageUrls() {
  try {
    const functions = getFunctions();
    const updateUrls = httpsCallable(functions, 'updateContractImageUrls');
    
    const result = await updateUrls();
    return result.data;
  } catch (error) {
    console.error('Error al actualizar URLs de contratos:', error);
    throw error;
  }
}

// Función para regenerar URLs de imágenes específicas
export async function regenerateImageUrls(imagePaths) {
  try {
    const functions = getFunctions();
    const regenerateUrls = httpsCallable(functions, 'regenerateImageUrls');
    
    const result = await regenerateUrls({ imagePaths });
    return result.data;
  } catch (error) {
    console.error('Error al regenerar URLs:', error);
    throw error;
  }
}

// Nombre del bucket de Firebase Storage
const BUCKET_NAME = 'contratosano-6138c.appspot.com';

/**
 * Genera una URL pública directa sin firmas
 * @param {string} path - Ruta del archivo en Firebase Storage
 * @returns {string} URL pública directa
 */
export function generatePublicUrl(path) {
  if (!path) return '';
  
  // Si ya es una URL pública directa, devolverla tal como está
  if (path.startsWith(`https://storage.googleapis.com/${BUCKET_NAME}/`)) {
    return path;
  }
  
  // Si contiene parámetros de firma, convertir a URL pública
  if (path.includes('GoogleAccessId') || path.includes('Expires') || path.includes('Signature')) {
    const urlParts = path.split('?')[0]; // Remover parámetros
    const pathPart = urlParts.split(`${BUCKET_NAME}/`)[1]; // Extraer la ruta después del bucket
    
    if (pathPart) {
      return `https://storage.googleapis.com/${BUCKET_NAME}/${pathPart}`;
    }
  }
  
  // Si es solo la ruta, construir la URL completa
  if (!path.startsWith('http')) {
    // Eliminar la barra inicial si existe
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `https://storage.googleapis.com/${BUCKET_NAME}/${cleanPath}`;
  }
  
  return resolveColivingImageUrl(path);
}

/**
 * Convierte un array de URLs de imágenes a URLs públicas
 * @param {string[]} imageUrls - Array de URLs de imágenes
 * @returns {string[]} Array de URLs públicas
 */
export function convertToPublicUrls(imageUrls) {
  if (!Array.isArray(imageUrls)) return [];
  return imageUrls.map(generatePublicUrl).filter(url => url);
}

/**
 * Función para subir archivos y hacerlos públicos automáticamente
 * @param {File} file - Archivo a subir
 * @param {string} path - Ruta donde subir el archivo
 * @returns {Promise<string>} URL pública del archivo subido
 */
export async function uploadFilePublic(file, path) {
  const { getStorage, ref, uploadBytes } = await import('firebase/storage');
  const storage = getStorage();
  
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  
  // Hacer el archivo público usando la Cloud Function
  const functions = getFunctions();
  const makePublic = httpsCallable(functions, 'regenerateImageUrls');
  
  try {
    await makePublic({ imagePaths: [path] });
  } catch (error) {
    console.warn('No se pudo hacer público automáticamente:', error);
  }
  
  // Devolver URL pública directa
  return generatePublicUrl(path);
} 